import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { Toaster } from "@/components/ui/sonner";

import appCss from "../styles.css?url";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page introuvable</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Cette page n'existe pas ou a été déplacée.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      // Defaults — overridden per-route
      { title: "RentaLMNP — Simulateur LMNP gratuit" },
      {
        name: "description",
        content:
          "Simulateur LMNP gratuit : calculez la rentabilité de votre investissement et comparez micro-BIC vs régime réel en 2 minutes.",
      },
      { name: "author", content: "RentaLMNP" },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { httpEquiv: "Content-Language", content: "fr-FR" },
      { property: "og:site_name", content: "RentaLMNP" },
      { property: "og:locale", content: "fr_FR" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      // Preload du CSS critique : le navigateur démarre le download dès le
      // parsing du <head>, sans attendre de découvrir le <link rel="stylesheet">.
      // Casse la chaîne critique HTML → CSS (gain ~150ms sur LCP).
      { rel: "preload", as: "style", href: appCss },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      // Preconnect Plausible — réduit la latence du script analytics.
      // Pas de `crossOrigin` : Plausible n'est pas une ressource CORS, le
      // navigateur ignorerait silencieusement le preconnect avec crossorigin.
      { rel: "preconnect", href: "https://plausible.io" },
      { rel: "dns-prefetch", href: "https://plausible.io" },
      { rel: "alternate", hrefLang: "fr-FR", href: "https://rentalmnp.com/" },
      { rel: "alternate", hrefLang: "x-default", href: "https://rentalmnp.com/" },
    ],
    scripts: [
      {
        // Plausible Analytics — anonyme, sans cookie, sans IP stockée (RGPD-safe)
        // `async` + `defer` => non-bloquant, et l'échec éventuel (adblock, timeout
        // réseau) est silencieux pour ne pas polluer la console / Lighthouse.
        src: "https://plausible.io/js/script.js",
        async: true,
        defer: true,
        "data-domain": "rentalmnp.fr,rentalmnp.lovable.app,rentalmnp.com",
        onerror: "this.setAttribute('data-failed','1')",
      },
      {
        children:
          "window.plausible = window.plausible || function() { (window.plausible.q = window.plausible.q || []).push(arguments) }",
      },
      // JSON-LD : WebApplication — décrit le simulateur pour Google & IA
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "RentaLMNP",
          alternateName: "Simulateur LMNP",
          url: "https://rentalmnp.com",
          description:
            "Simulateur français gratuit de rentabilité LMNP. Calcule cash-flow, rendement net, score d'investissement et compare automatiquement les régimes fiscaux.",
          applicationCategory: "FinanceApplication",
          applicationSubCategory: "Investment Calculator",
          operatingSystem: "Web",
          browserRequirements: "Requires JavaScript. Requires HTML5.",
          inLanguage: "fr-FR",
          isAccessibleForFree: true,
          offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
          creator: {
            "@type": "Organization",
            name: "RentaLMNP",
            url: "https://rentalmnp.com",
          },
          audience: {
            "@type": "Audience",
            audienceType: "Real Estate Investors",
          },
          featureList: [
            "Calcul de cash-flow mensuel",
            "Rendement net après impôts",
            "Score d'investissement sur 100",
            "Comparaison régime réel vs micro-BIC",
            "Projection patrimoniale 10 ans",
            "Calcul d'impôts par tranche TMI",
            "Export PDF de la simulation",
          ],
        }),
      },
      // JSON-LD : Organization — identité éditoriale
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "RentaLMNP",
          url: "https://rentalmnp.com",
          logo: "https://rentalmnp.com/favicon.png",
          description:
            "Le simulateur français de référence pour évaluer la rentabilité d'un investissement LMNP.",
          areaServed: "FR",
          knowsAbout: [
            "LMNP",
            "Loueur Meublé Non Professionnel",
            "Investissement immobilier",
            "Fiscalité immobilière",
            "Régime réel simplifié",
            "Micro-BIC",
            "Amortissement LMNP",
            "Cash-flow immobilier",
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="fr">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <>
      <Outlet />
      <Toaster position="top-center" richColors />
    </>
  );
}
